import * as sessionReplay from "@amplitude/session-replay-browser";
import { env } from 'src/env.mjs';
import { EventProps } from './EventProps';
import { useUser } from 'src/hooks/User/useUser';
import { useEffect } from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { capitalise, getUserUuid } from '@get-mosh/msh-shared';
import Brands from 'src/constants/Brands';
import { datadogRum } from '@datadog/browser-rum';

const analytics = AnalyticsBrowser.load(
    {
        writeKey: env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
        cdnURL: env.NEXT_PUBLIC_SEGMENT_CDN_URL,
    },
    {
        integrations: {
            'Segment.io': {
                apiHost: env.NEXT_PUBLIC_SEGMENT_API_HOST,
            },
        },
    },
);

export const identify = (
    userId: string,
    traits?: { [key: string]: string },
) => {
    const uuid = getUserUuid();

    analytics.identify(userId, {
        brand: env.NEXT_PUBLIC_BRAND,
        uuid,
        event_source: `${capitalise(env.NEXT_PUBLIC_BRAND || Brands.MOSH)} Website`,
        country_code: env.NEXT_PUBLIC_COUNTRY_CODE,
        ...traits,
    });
};

export const page = (name: string, category = '', properties?: EventProps) => {
    const uuid = getUserUuid();

    analytics.page(category, name, {
        ...properties,
        uuid,
        event_source: `${capitalise(env.NEXT_PUBLIC_BRAND || Brands.MOSH)} Website`,
        brand: env.NEXT_PUBLIC_BRAND,
        country_code: env.NEXT_PUBLIC_COUNTRY_CODE,
    });
};

export const track = (
    eventName: string,
    properties?: EventProps,
    callback?: () => void,
) => {
    const uuid = getUserUuid();

    analytics.track(
        eventName,
        {
            uuid,
            brand: env.NEXT_PUBLIC_BRAND,
            url: window.location.href,
            path: window.location.pathname,
            event_source: `${capitalise(env.NEXT_PUBLIC_BRAND || Brands.MOSH)} Website`,
            country_code: env.NEXT_PUBLIC_COUNTRY_CODE,
            ...properties,
        },
        callback,
    );
};

export const setAnonymousId = (anonymousId: string) => {
    analytics.setAnonymousId(anonymousId);
};

export const addTraits = (traits: { [key: string]: string }) => {
    analytics.identify(traits);
};

export const getAnalyticsUser = async () => {
    return await analytics.user();
};

const initAmplitudeSessionReplay = async () => {
    const user = await getAnalyticsUser();
    await sessionReplay.init(env.NEXT_PUBLIC_AMPLITUDE_API_KEY, {
        sessionId: user.anonymousId() || undefined,
        sampleRate: env.NEXT_PUBLIC_COUNTRY_CODE === 'NZ' ? 1 : 0.2, // 100% for NZ, 20% for AU
    }).promise;

    // Add middleware to always add session replay properties to track calls
    analytics.addSourceMiddleware(({ payload, next }) => {
        const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
        if (payload.type() === "track") {
            payload.obj.properties = {
            ...payload.obj.properties,
            ...sessionReplayProperties,
            };
        }
        next(payload);
    });
}

type AnalyticsProviderProps = {
    children: React.ReactNode;
};

function AnalyticsProvider({ children }: AnalyticsProviderProps) {
    const { data } = useUser();
    const identify = (userId: string, traits: Record<string, string>) => {
        analytics.identify(userId, traits);
    };

    useEffect(() => {
        if (!data) return;

        identify(data.id, {
            uuid: data.uuid,
            brand: env.NEXT_PUBLIC_BRAND,
            email: data.email,
            phone: data.phoneNumber,
            country_code: data.countryCode,
        });

        datadogRum.setUser({
            id: data.id,
            uuid: data.uuid,
        });

        initAmplitudeSessionReplay();
    }, [data]);

    return <>{children}</>;
}

export default AnalyticsProvider;
